import { c as _c } from "react/compiler-runtime";
import { Button, IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { css } from '@repo/panda/css';
import type { WebLoginPromptTrigger } from '@repo/types/analytic-events/website-analytic-events';
import type React from 'react';
import { useCallback } from 'react';
import { MapsAuthModal } from '../components/maps-auth-modal';
import { heroImageLayerStyles, scrollFadeBottomStyles, scrollFadeTopStyles, sidebarHeroFrameStyles, sidebarScrollAreaStyles } from './sidebar-styles';
import { useSidebarScrollState } from './use-sidebar-scroll-state';
interface SidebarHeroFrameProps {
  backgroundImageUrl: string;
  backgroundImageStyleOverride?: React.CSSProperties;
  isMobilePane: boolean;
  onBack: () => void;
  backLabel: string;
  searchAriaLabel: string;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  isLoginModalOpen: boolean;
  onLoginModalOpenChange: (open: boolean) => void;
  loginModalTrigger: WebLoginPromptTrigger;
  onSearchFocus?: () => void;
  gap?: '8' | '10' | '12' | '16';
  /** Optional action element rendered top-right in the sticky header, right of the pinned-search button when both are shown. */
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Shared sidebar shell: dim/gradient hero frame, full-sidebar scroll, sticky back/search
 * header, top/bottom scroll-fade overlays, and the auth modal mount. Used by both the
 * Explore sidebar and the 100% checklist; planned third consumer is the AI-generated
 * checklist view.
 *
 * Caller owns `searchInputRef` (pass it to the underlying SearchFilters inside children).
 * The frame observes that ref to toggle the pinned search icon and focuses it when the
 * icon is clicked.
 */
export const SidebarHeroFrame: React.FC<SidebarHeroFrameProps> = t0 => {
  const $ = _c(49);
  const {
    backgroundImageUrl,
    backgroundImageStyleOverride,
    isMobilePane,
    onBack,
    backLabel,
    searchAriaLabel,
    searchInputRef,
    isLoginModalOpen,
    onLoginModalOpenChange,
    loginModalTrigger,
    onSearchFocus,
    gap: t1,
    headerAction,
    children
  } = t0;
  const gap = t1 === undefined ? "12" : t1;
  let t2;
  if ($[0] !== searchInputRef) {
    t2 = {
      pinSentinelRef: searchInputRef
    };
    $[0] = searchInputRef;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    scrollRef,
    isPinned,
    showTopFade,
    showBottomFade
  } = useSidebarScrollState(t2);
  let t3;
  if ($[2] !== onSearchFocus || $[3] !== scrollRef || $[4] !== searchInputRef) {
    t3 = () => {
      scrollRef.current?.scrollTo({
        top: 0,
        behavior: "smooth"
      });
      searchInputRef.current?.focus({
        preventScroll: true
      });
      onSearchFocus?.();
    };
    $[2] = onSearchFocus;
    $[3] = scrollRef;
    $[4] = searchInputRef;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const scrollToSearch = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = css(sidebarHeroFrameStyles);
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== backgroundImageStyleOverride || $[8] !== backgroundImageUrl) {
    t5 = backgroundImageUrl && <div aria-hidden="true" className={heroImageLayerStyles} style={{
      backgroundImage: `url(${backgroundImageUrl})`,
      ...backgroundImageStyleOverride
    }} />;
    $[7] = backgroundImageStyleOverride;
    $[8] = backgroundImageUrl;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== isMobilePane) {
    t6 = css.raw({
      position: "sticky",
      top: "0",
      zIndex: 3,
      paddingTop: isMobilePane ? "24" : "0"
    });
    $[10] = isMobilePane;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = css({
      backdropFilter: "[blur(10px)]"
    });
    t8 = <Icon icon="arrow_back" decorative={true} />;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  let t9;
  if ($[14] !== backLabel || $[15] !== onBack) {
    t9 = <Button variant="filled" onClick={onBack} color="secondary" className={t7}>{t8}{backLabel}</Button>;
    $[14] = backLabel;
    $[15] = onBack;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== headerAction || $[18] !== isPinned || $[19] !== scrollToSearch || $[20] !== searchAriaLabel) {
    t10 = (headerAction || isPinned) && <FlexRow gap="8" align="center">{isPinned && <IconButton variant="filled" onClick={scrollToSearch} color="primary" aria-label={searchAriaLabel}><Icon icon="search" decorative={true} /></IconButton>}{headerAction}</FlexRow>;
    $[17] = headerAction;
    $[18] = isPinned;
    $[19] = scrollToSearch;
    $[20] = searchAriaLabel;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t6 || $[24] !== t9) {
    t11 = <FlexRow justify="between" align="center" width="full" data-drawer-drag="true" className={t6}>{t9}{t10}</FlexRow>;
    $[22] = t10;
    $[23] = t6;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== children || $[27] !== gap || $[28] !== t11) {
    t12 = <FlexColumn gap={gap} width="full">{t11}{children}</FlexColumn>;
    $[26] = children;
    $[27] = gap;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== scrollRef || $[31] !== t12) {
    t13 = <div ref={scrollRef} className={sidebarScrollAreaStyles}>{t12}</div>;
    $[30] = scrollRef;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== showTopFade) {
    t14 = showTopFade && <div className={scrollFadeTopStyles} />;
    $[33] = showTopFade;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== showBottomFade) {
    t15 = showBottomFade && <div className={scrollFadeBottomStyles} />;
    $[35] = showBottomFade;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== onLoginModalOpenChange) {
    t16 = details => onLoginModalOpenChange(details.open);
    $[37] = onLoginModalOpenChange;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== isLoginModalOpen || $[40] !== loginModalTrigger || $[41] !== t16) {
    t17 = <MapsAuthModal isOpen={isLoginModalOpen} onOpenChange={t16} trigger={loginModalTrigger} size="sm" />;
    $[39] = isLoginModalOpen;
    $[40] = loginModalTrigger;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t13 || $[44] !== t14 || $[45] !== t15 || $[46] !== t17 || $[47] !== t5) {
    t18 = <div className={t4}>{t5}{t13}{t14}{t15}{t17}</div>;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
    $[46] = t17;
    $[47] = t5;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  return t18;
};